import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { RiArrowDownSLine, RiCheckLine, RiCloseLine } from 'react-icons/ri';
import Select, { components } from 'react-select';

function DropdownSelect({
  onChange,
  options,
  placeholder,
  isClearable,
  isSearchable,
  isMulti,
  isValid,
  isInvalid,
  isRequired,
  value,
  errorMessage,
  onBlur
}) {
  const messageClassName = useMemo(
    () => ['left-0', 'bottom-0', 'translate-y-full', 'text-xs'].join(' '),
    []
  );

  const getHelperText = useMemo(() => {
    if (isInvalid && errorMessage !== '') {
      return (
        <span className={`${messageClassName} text-cinnebar peer-focus:hidden`}>
          {errorMessage}
        </span>
      );
    }
    return null;
  }, [errorMessage, isInvalid]);

  const getColor = () => {
    if (isValid) {
      return '#0CD428';
    }
    if (isInvalid) {
      return '#DB3F39';
    }
    return 'rgb(204 204 204 / 1)';
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  function CaretDownIcon() {
    if (isValid) {
      return <RiCheckLine className="h-6 w-6 text-green" />;
    }
    if (isInvalid) {
      return <RiCloseLine className="h-6 w-6 text-cinnebar" />;
    }

    return <RiArrowDownSLine className="w-10 text-night-rider" />;
  }

  // eslint-disable-next-line react/no-unstable-nested-components
  function DropdownIndicator(props) {
    return (
      <components.DropdownIndicator {...props}>
        <CaretDownIcon />
      </components.DropdownIndicator>
    );
  }

  const style = {
    control: base => ({
      ...base,
      'height': '54px',
      'padding': '0px 8px 0px 8px',
      'borderRadius': '0px !important',
      'border': '1px solid border-gray',
      'borderColor': getColor(),
      'boxShadow': 'none',
      '&:hover': '1px solid black',
      'fontSize': '16px',
      '@media screen and (min-width: 768px)': {
        fontSize: '14px'
      }
    })
  };

  return (
    <div>
      {isRequired ? (
        <Select
          components={{ DropdownIndicator, IndicatorSeparator: () => null }}
          onChange={onChange}
          onBlur={onBlur}
          options={options}
          placeholder={
            <div>
              {placeholder}
              <span style={{ color: '#db3f39' }}> *</span>
            </div>
          }
          isClearable={isClearable}
          isSearchable={isSearchable}
          isMulti={isMulti}
          styles={style}
          value={value}
          theme={theme => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary25: '#F2F2F2',
              primary: '#F2F2F2'
            }
          })}
        />
      ) : (
        <Select
          components={{ DropdownIndicator, IndicatorSeparator: () => null }}
          onChange={onChange}
          onBlur={onBlur}
          options={options}
          placeholder={placeholder}
          isClearable={isClearable}
          isSearchable={isSearchable}
          isMulti={isMulti}
          styles={style}
          value={value}
          theme={theme => ({
            ...theme,
            colors: {
              ...theme.colors,
              primary25: '#F2F2F2',
              primary: '#F2F2F2'
            }
          })}
        />
      )}
      {getHelperText}
    </div>
  );
}

DropdownSelect.propTypes = {
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.node
    })
  ).isRequired,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isMulti: PropTypes.bool,
  isValid: PropTypes.bool,
  isInvalid: PropTypes.bool,
  /* react-select has a Custom Type for the Value Property, and, thus, we would like to keep this Any. */
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  errorMessage: PropTypes.string
};

DropdownSelect.defaultProps = {
  onChange: () => {},
  onBlur: () => {},
  placeholder: '',
  isClearable: false,
  isSearchable: true,
  isMulti: false,
  isValid: false,
  isInvalid: false,
  errorMessage: '',
  value: ''
};

export default DropdownSelect;
